import * as React from "react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Table, Button, Input, Select, Modal, Alert, Form, Typography, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import TablePagination from './TablePagination';

import EntityInfo from "../types/EntityInfo";
import { deletePlace, fetchPlaces, postPlace } from "../api/places";
import { fetchCities } from "../api/city";
import PlaceRequest from "../types/PlaceRequest";
import Place from "../types/Place";
import { useTexts } from "../hooks/useTexts";
import { getUserFromToken } from "../data/storage";
import UserRole from "../types/UserRole";
import { selectFilterOption } from "../util/common";

const { Title, Text } = Typography;

const Places = () => {
    const { t } = useTexts();
    const [places, setPlaces] = useState<Place[]>([]);
    const [cities, setCities] = useState<EntityInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [searchName, setSearchName] = useState("");
    const [open, setOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<Place>({ id: 0, name: '', city: '' });
    const [form] = Form.useForm();

    const userData = getUserFromToken();
    const isManager = userData?.roles?.some(role => role.id === UserRole.MANAGER);

    const getPlaces = (currentPage = page, currentLimit = limit, currentName = searchName) => {
        setLoading(true);
        fetchPlaces({ page: currentPage, limit: currentLimit, name: currentName }).then(response => {
            if (response && response.content) {
                setPlaces(response.content);
                if (response.pagination) {
                    setTotal(response.pagination.elementCount);
                    setPageCount(response.pagination.pageCount);
                } else {
                    setTotal(response.content.length);
                    setPageCount(1);
                }
            } else {
                setPlaces([]);
                setTotal(0);
                setPageCount(0);
            }
        })
            .catch(error => console.error("ERROR", error))
            .finally(() => setLoading(false));
    }

    const getCities = () => {
        fetchCities().then(response => {
            setCities(response?.items || []);
        })
            .catch(error => console.error("ERROR", error));
    }

    const openModal = () => setOpen(true);
    const closeModal = () => setOpen(false);

    const removePlace = (id: number) => {
        deletePlace(id).then(() => {
            closeModal();
            getPlaces();
        })
            .catch(error => console.error("ERROR", error));
    }

    useEffect(() => {
        getCities();
    }, []);

    useEffect(() => {
        getPlaces(page, limit, searchName);
    }, [page, limit, searchName]);

    const handleFinish = (values: { name: string, cityId: number }): void => {
        setErrors([]);
        setSubmitting(true);
        const placeRequest: PlaceRequest = { name: values.name, cityId: values.cityId };
        postPlace(placeRequest)
            .then(response => {
                if (response?.id) {
                    getPlaces();
                    toast.success(t('place', 'placeAdded'), { theme: "colored" });
                    form.resetFields();
                } else if (!!response?.messages?.length && response?.messages?.length > 0) {
                    setErrors(response.messages);
                }
            })
            .catch(error => console.error("ERROR", error))
            .finally(() => setSubmitting(false));
    };

    const columns: ColumnsType<Place> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => index + 1,
            width: 60,
        },
        {
            title: t('place', 'placeName'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('place', 'city'),
            dataIndex: 'city',
            key: 'city',
        }
    ];

    if (!isManager) {
        columns.push({
            title: '',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Button
                    danger
                    type="primary"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setRowToDelete(record);
                        openModal();
                    }}
                >
                    {t('common', 'delete')}
                </Button>
            )
        });
    }

    return (
        <div style={{ marginTop: '50px' }}>
            <Modal
                title={`'${rowToDelete?.name}' ${t('product', 'deleteConfirm')}`}
                open={open}
                onCancel={() => {
                    setRowToDelete({ id: 0, name: '', city: '' });
                    closeModal();
                }}
                onOk={() => removePlace(rowToDelete?.id)}
                okText={t('common', 'yes')}
                cancelText={t('common', 'close')}
                okButtonProps={{ danger: true }}
            >
                <Text>{t('place', 'deleteConfirm', { name: rowToDelete?.name })}</Text>
            </Modal>

            {errors?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {errors.map((err, idx) => (
                        <Alert key={idx} message={err} type="error" showIcon style={{ marginBottom: '8px' }} />
                    ))}
                </div>
            )}

            <Input.Search
                placeholder={t('place', 'searchByName') || 'Search by name...'}
                allowClear
                onSearch={(value) => {
                    setSearchName(value);
                    setPage(1);
                }}
                style={{ width: 250, marginBottom: 24, marginRight: 24 }}
            />

            <Form
                form={form}
                layout="inline"
                onFinish={handleFinish}
                style={{ marginBottom: '24px' }}
            >
                <Form.Item
                    name="name"
                    rules={[{ required: true, message: 'Please enter a place name' }]}
                    style={{ minWidth: 220, marginBottom: 24 }}
                >
                    <Input placeholder={t('place', 'placeName')} disabled={isManager} />
                </Form.Item>
                <Form.Item
                    name="cityId"
                    rules={[{ required: true, message: 'Please select a city' }]}
                    style={{ minWidth: 220, marginBottom: 24 }}
                >
                    <Select
                        placeholder={t('place', 'city')}
                        disabled={isManager}
                        allowClear
                        showSearch
                        filterOption={selectFilterOption}
                        options={cities.map(option => ({ label: option.name, value: option.id }))}
                    />
                </Form.Item>
                <Form.Item style={{ marginBottom: 24 }}>
                    <Button type="primary" htmlType="submit" loading={submitting} disabled={isManager} icon={<PlusOutlined />}>
                        {t('place', 'addPlace')}
                    </Button>
                </Form.Item>
            </Form>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : places?.length > 0 ? (
                <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <Table
                        columns={columns}
                        dataSource={places}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                    <TablePagination
                        current={page}
                        pageSize={limit}
                        pageCount={pageCount}
                        total={total}
                        onChange={(p, s) => {
                            setPage(p);
                            if (s !== limit) setLimit(s);
                        }}
                    />
                </div>
            ) : (
                <Title level={4}>{t('place', 'placeNotFound')}</Title>
            )}
        </div>
    )
}

export default Places;